/**
 * school-density.ts — derive a "how many schools sit in this suburb"
 * signal from the existing `SCHOOL_RANKS` dataset.
 *
 * Why this exists:
 *   The buyer-evaluator question we are trying to answer in <30 seconds
 *   is "should I trust this place?". One concrete contributor to that
 *   answer is "is there real schooling here, or do families have to
 *   travel?". The answer is *already* implicit in `school-ranks.ts` —
 *   we simply count the rows per `suburbId` and bucket. No new data,
 *   no scraping, no fake coordinates.
 *
 * Scope rules — read these before touching this file:
 *   1. Pure derivation. No side effects. No network. No score change.
 *      Verdict colour and `confidenceScore` are NEVER recomputed from
 *      density — that's a separate decision that requires its own
 *      product call. This helper is descriptive, not prescriptive.
 *   2. Thresholds (HIGH ≥5, MEDIUM 2–4, LOW 0–1) come from the brief.
 *      They are the ONLY thresholds — don't add a "very high" tier or
 *      a per-city tweak without a paired UX change. Three buckets keep
 *      the visual signal honest at a glance.
 *   3. We count whatever rows the dataset has for a suburb. We do not
 *      filter by `kind` (public/private), `level` (primary/secondary),
 *      or `curriculum` here. Those filters are decision-context that
 *      belong upstream of this helper if/when we add them.
 *   4. Coverage caveat: SCHOOL_RANKS is a curated bank, not an
 *      exhaustive census. A suburb showing LOW may simply be
 *      undocumented in our dataset — see `coverageNote()` for the
 *      string the UI must surface alongside the verdict bullet.
 */

import { SCHOOL_RANKS } from "./school-ranks";

export type SchoolDensityLevel = "high" | "medium" | "low";

export interface SchoolDensity {
  /** Number of `SCHOOL_RANKS` rows whose `suburbId` matches the input. */
  count: number;
  /** Bucketed level — see thresholds in module header. */
  level: SchoolDensityLevel;
}

/* -------------------------------------------------------------------------- */
/*  Cache                                                                     */
/*                                                                            */
/*  SCHOOL_RANKS is built once at module load (it applies the monthly         */
/*  override on top of the static bank). The per-suburb count is therefore    */
/*  fixed for the session — caching it avoids re-scanning the array on        */
/*  every map render and every verdict-card open.                             */
/*                                                                            */
/*  The cache is keyed by `suburbId` (e.g., "mel-balwyn", "gachibowli").      */
/*  Both MEL and HYD share this map because suburb ids are city-prefixed      */
/*  for MEL and unique strings for HYD — they don't collide.                  */
/* -------------------------------------------------------------------------- */

let _countsBySuburb: Map<string, number> | null = null;

function ensureCounts(): Map<string, number> {
  if (_countsBySuburb) return _countsBySuburb;
  const m = new Map<string, number>();
  for (const s of SCHOOL_RANKS) {
    m.set(s.suburbId, (m.get(s.suburbId) ?? 0) + 1);
  }
  _countsBySuburb = m;
  return m;
}

/** HIGH ≥5 schools, MEDIUM 2–4, LOW 0–1 — fixed by brief. */
export function schoolDensityLevel(count: number): SchoolDensityLevel {
  if (count >= 5) return "high";
  if (count >= 2) return "medium";
  return "low";
}

/**
 * Look up density for a suburb id (matches `Verdict.id`).
 * A suburb with zero rows is LOW with `count: 0`. We do NOT return a
 * "no data" tier — the user-facing copy makes the coverage caveat
 * explicit at the call site (see `coverageNote`).
 */
export function getSchoolDensity(suburbId: string): SchoolDensity {
  const count = ensureCounts().get(suburbId) ?? 0;
  return { count, level: schoolDensityLevel(count) };
}

/* -------------------------------------------------------------------------- */
/*  User-facing copy                                                          */
/*                                                                            */
/*  Centralised here so the map legend, the verdict card line, and the        */
/*  reason bullet stay in lockstep. If product wants to retune wording they   */
/*  edit one file.                                                            */
/* -------------------------------------------------------------------------- */

/** "High" / "Medium" / "Low" — used in the "School presence: ___" card line. */
export function densityLabel(level: SchoolDensityLevel): string {
  switch (level) {
    case "high":   return "High";
    case "medium": return "Medium";
    case "low":    return "Low";
  }
}

/** One-line description for the verdict card body. Matches the brief's
 *  wording so users see the same phrasing in legend + card + bullet. */
export function densityHint(level: SchoolDensityLevel): string {
  switch (level) {
    case "high":
      return "Dense schooling cluster — strong for families";
    case "medium":
      return "Several schools nearby — workable for most families";
    case "low":
      return "Weak nearby schooling — schools require travel";
  }
}

/** Bullet copy appended to verdict reasons. Shorter than the hint —
 *  reasons render as compact dotted bullets, so we trim. */
export function densityReasonBullet(level: SchoolDensityLevel): string {
  switch (level) {
    case "high":   return "Strong school density nearby";
    case "medium": return "Mixed school density nearby";
    case "low":    return "Weak schooling access";
  }
}

/** Coverage caveat. SCHOOL_RANKS is curated, not exhaustive — a "low"
 *  result can mean "no data here yet" rather than "no schools here".
 *  The card surfaces this in small text under the school-presence line. */
export function coverageNote(): string {
  return "Counted from our school-rank dataset; coverage is partial in some suburbs.";
}
